import upperFirst from 'lodash/upperFirst';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { cssVars } from '../../app/styles';
import { DATE_TODAY, subtypeChartLabels } from '../../constants';
import {
  DATE_ONE_WEEK_AGO,
  NEW_LISTING,
  UPDATED_LISTING,
} from '../../list/list-constants';
import { InspectionText } from '../../property-inspections/constants';
import { BaseColDefs } from './base-col-defs';
import {
  TCustomColumnDefinition,
  TCustomColumnDefinitions,
} from './listing-col-defs-types';
import { TColumn } from './base-col-defs-types';

const LISTING_ID = 'listingId';

const millisecondsInDay = 1000 * 3600 * 24;

const validVerifiedValues = new Set(['verified', 'unverified', 'pending']);

export class ListingColDefs extends BaseColDefs {
  private params: unknown;

  // See src/list/list-selectors.js keyMappings selector for the source of data. rentalTypes seems to be coming from
  // rentalscape config.
  //
  // See
  // https://github.com/deckardtech/customer-config/blob/bc4313d2d557fdc635d803c9ad119faa4ab9b592/customer_config/schemas/rentalscape.py#L46
  private keyMappings: {
    rentalType: {
      current: string;
      hotel: string;
      ltr: string;
      other: string;
      previous: string;
      unavailable: string;
    };
  };

  private totAlias: unknown;

  // Set by subclassees, e.g. LicenseColDefs.
  protected licenseColumnFieldName: string;

  // Set by subclassees, e.g. InspectionsColDefs.
  protected licenseDisplayedColumnFieldName: string;

  // for extracting and displaying the first out of multiple possible violations
  static FIRST_VIOLATION_INDEX = 0;

  static calendarColId = 'calendar';

  /**
   * Documentation: https://www.ag-grid.com/react-grid/excel-export-api/#excelstyle
   * Examples: https://www.ag-grid.com/react-grid/excel-export-styles/
   * Each object is the equivalent of a CSS class in Excel being applied to a cell
   * with that id as the cellClass
   */
  static excelStyles = [
    {
      id: 'hyperlinks',
      font: {
        underline: 'Single',
        color: '#0000ff',
      },
    },
    {
      id: 'header',
      alignment: {
        horizontal: 'Center',
        vertical: 'Center',
        wrapText: true,
      },
      interior: {
        color: '#dddddd',
        pattern: 'Solid',
      },
      borders: {
        borderBottom: { color: '#000000', lineStyle: 'Continuous', weight: 1 },
        borderLeft: { color: '#000000', lineStyle: 'Continuous', weight: 1 },
        borderRight: { color: '#000000', lineStyle: 'Continuous', weight: 1 },
        borderTop: { color: '#000000', lineStyle: 'Continuous', weight: 1 },
      },
      font: {
        bold: true,
        size: 15,
      },
    },
    {
      id: 'cell',
      alignment: {
        horizontal: 'Center',
        vertical: 'Center',
        wrapText: true,
      },
    },
    {
      /*
        This is necessary so that long listing IDs aren't
        displayed as numbers in scientific notation when Excel
        displays the exported file
       */
      id: LISTING_ID,
      dataType: 'String',
    },
    {
      id: 'apn-cell-class',
      // Ensures leading zeros in APNs are not lost.
      dataType: 'String',
    },
  ];

  /**
   * This function generates an ag-grid column definition for displaying a gantt chart month
   *
   * @param {*} params
   * @param {string} params.monthName
   * @param {number} params.monthIndex
   * @param {Number} params.numDays number of days in the month
   * @param {Number} params.firstDayNumber number value of the date portion of months DateTime
   *  (date key)
   */
  // static calendarActivitiesColGroup(ganttMonthDetails) {
  //   const {
  //     monthName,
  //     monthIndex,
  //     numDays,
  //     firstDayNumber,
  //     weekendsInMonth
  //   } = ganttMonthDetails;

  //   return {
  //     headerName: `${monthName} - Booking Activities`,
  //     children: [{
  //       valueGetter: ({ data }) => {
  //         const dataPoints = data?.dataPoints;
  //         // TODO: Should be `return dataPoints?.[monthIndex].calendarActivity;` after fix data
  //         return dataPoints?.[monthIndex]?.calendarActivity;
  //       },
  //       cellRenderer: 'gantRowCellRenderer',
  //       colId: ListingColDefs.calendarColId,
  //       cellRendererParams: { weekendsInMonth },
  //       width: numDays * GANT_DAY_BOX_WIDTH,
  //       headerComponentFramework: GantHeaderCell,
  //       headerComponentParams: { numDays, firstDayNumber },
  //       cellStyle: {
  //         paddingLeft: 0,
  //         paddingRight: 0,
  //       },
  //     }],
  //   };
  // }

  // static ganttMonthColumns({ ganttMonthDetails }) {
  //   return ganttMonthDetails
  //     .map((monthDetails) => ListingColDefs.calendarActivitiesColGroup(monthDetails));
  // }

  static diffStatusFactory(data) {
    if (!data) return null;

    const lastModifiedDate = new Date();
    const olderThanOneWeek = DATE_ONE_WEEK_AGO > lastModifiedDate;

    if (olderThanOneWeek) return null;

    // TODO: Temporary.. Keep this only while isNewListing is not mandatory in the schema
    if (typeof data.isNewListing !== 'boolean') return null;

    return data.isNewListing ? NEW_LISTING : UPDATED_LISTING;
  }

  static lastModifiedValueGetter(data) {
    if (!data?.lastModified) return null;

    const lastModifiedDate = new Date(data.lastModified);
    const millisecondsPassed =
      DATE_TODAY.getTime() - lastModifiedDate.getTime();
    const daysPassedFloat = millisecondsPassed / millisecondsInDay;
    const daysPassedStr = daysPassedFloat.toFixed();
    const pluralS = daysPassedStr === '1' ? '' : 's';
    return `${daysPassedStr} day${pluralS} ago`;
  }

  static liveFilterComparisonValue(value) {
    if (value === ListingColDefs.platformKeys.others) return 'zzz0';
    if (value === ListingColDefs.platformKeys.null) return 'zzz1';
    if (value === 'true') return 'zzz2';
    if (value === 'false') return 'zzz3';
    return value;
  }

  static booleanLiveToStringGetter = {
    true: 'Live',
    false: 'Not-Live',
  };

  static platformKeys = {
    airbnb: 'airbnb',
    vrbo: 'vrbo',
    homeaway: 'homeaway',
    flipkey: 'flipkey',
    others: 'others',
    null: 'None',
  };

  static liveColumnPlatformKeyFactory(platform) {
    const platformKey = ListingColDefs.platformKeys[platform];
    return platformKey || ListingColDefs.platformKeys.others;
  }

  constructor(params) {
    super();
    this.params = params;
    this.keyMappings = params?.keyMappings;
    this.totAlias = params?.totAlias;
  }

  // determines whether group nodes should have checkboxes. Override to use checkboxes
  groupNodesWithCheckboxes = false;

  leafNodesWithCheckboxes = false;

  /*
   * ---------------------------------
   * ------------ columns ------------
   * ---------------------------------
   */

  /**
   *  Sets up columns and groups in the grid
   *
   * @abstract
   */
  columnGroups() {
    // eslint-disable-line class-methods-use-this
    return null;
  }

  /**
   * This function is meant to be used as a callback that determines whether a
   * cell should have a checkbox or not.
   *
   * @param {*} params These are params passed by agGrid when generating cell
   */
  shouldDisplayCheckbox(params) {
    if (this.groupNodesWithCheckboxes) {
      return params.node.group === true;
    }
    if (this.leafNodesWithCheckboxes) {
      return !params.node.group === true;
    }
    return false;
  }

  /** Determines leaf values in group column (not group parent data itself) */
  autoGroupColDef = this.textColumn({
    pinned: 'left',
    headerName: 'Address',
    field: 'address',
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      innerRenderer: 'addrCellRenderer',
      // checkbox: this.shouldDisplayCheckbox.bind(this),
      suppressCount: true,
    },
    // headerCheckboxSelection: true,
    // headerCheckboxSelectionFilteredOnly: true,
    filter: true,
  });

  // ----- propertyDetailsColGroup -----

  addressColumn = this.textColumn({
    headerName: 'Address',
    field: 'address',
    colId: 'address',
    filter: 'agTextColumnFilter',
    // cell renderer will render address only
    cellRenderer: 'AddressCellRenderer',
    // headerCheckboxSelection: true,
    // headerCheckboxSelectionFilteredOnly: true,
    // checkboxSelection: true,
  });

  groupByAddressColumn = {
    ...this.addressColumn,
    // https://www.ag-grid.com/javascript-data-grid/grouping-complex-objects/#creating-group-keys-from-complex-objects
    keyCreator: (param) => param.data.deckardid,
    rowGroup: true,
    hide: true,
  };

  deckardIDColumn = this.textColumn({
    headerName: 'Deckard ID',
    field: 'deckardid',
    width: 250,
    hide: true,
  });

  unitNumberColumn = this.textColumn({
    headerName: 'Unit Number',
    field: 'unitNumber',
    width: 135,
    hide: false,
  });

  streetAddressColumn = this.textColumn({
    headerName: 'Street Address',
    field: 'streetAddress',
    hide: true,
  });

  cityColumn = this.textColumn({
    headerName: 'City',
    field: 'city',
    width: 135,
    hide: true,
  });

  stateColumn = this.textColumn({
    headerName: 'State',
    field: 'state',
    width: 135,
    hide: true,
  });

  zipColumn = this.textColumn({
    headerName: 'Zip Code',
    field: 'zip',
    width: 135,
    hide: true,
  });

  ownerUnitNumberColumn = this.textColumn({
    headerName: 'Owner Unit Number',
    field: 'ownerUnitNumber',
    width: 135,
    hide: true,
  });

  ownerStreetAddressColumn = this.textColumn({
    headerName: 'Owner Street Address',
    field: 'ownerStreetAddress',
    hide: true,
  });

  ownerCityColumn = this.textColumn({
    headerName: 'Owner City',
    field: 'ownerCity',
    width: 135,
    hide: true,
  });

  ownerStateColumn = this.textColumn({
    headerName: 'Owner State',
    field: 'ownerState',
    width: 135,
    hide: true,
  });

  ownerZipColumn = this.textColumn({
    headerName: 'Owner Zip Code',
    field: 'ownerZip',
    width: 135,
    hide: true,
  });

  diffStatusColumn = this.setColumn({
    headerName: 'Status',
    field: 'lastModified',
    cellRenderer: 'diffStatusCellRenderer',
    valueGetter: ({ data }) => ListingColDefs.diffStatusFactory(data),
  });

  lastUpdatedColumn = this.textColumn({
    headerName: 'Last Updated',
    field: 'lastModified',
    valueGetter: ({ data }) => ListingColDefs.lastModifiedValueGetter(data),
    cellStyle: { color: cssVars.textColors.light3, fontFamily: 'Nunito Sans' },
  });

  apnColumn = this.textColumn({
    pinned: 'left',
    headerName: 'APN',
    field: 'apn',
    cellClass: 'apn-cell-class',
  });

  zoneColumn = this.textColumn({
    headerName: 'Zone',
    field: 'regions',
  });

  ownerColumn = this.textColumn({
    headerName: 'Owner',
    field: 'owner',
    filter: 'agTextColumnFilter',
  });

  ownerOccupiedColumn = this.textColumn({
    headerName: 'Owner Occupied',
    field: 'ownerOccupied',
    valueFormatter: ({ value }) => {
      if (value === true || value === false) {
        return subtypeChartLabels.ownerOccupied[value];
      }
      return value;
    },
  });

  residentialTypeColumn = this.setColumn({
    headerName: 'Residential Type',
    field: 'residentialType',
    refData: subtypeChartLabels.residentialType,
  });

  ownerMailingAddressColumn = this.textColumn({
    headerName: 'Owner Mailing Address',
    field: 'ownerAddress',
    cellStyle: {
      cursor: 'pointer',
      textOverflow: 'ellipsis',
    },
    tooltipField: 'ownerAddress',
    width: 250,
  });

  // ----- listingsInfoColGroup -----
  hostColumn = this.textColumn({
    headerName: 'Host',
    field: 'hostInfo.name',
    cellStyle: { cursor: 'pointer' },
  });

  verifiedColumn = this.setColumn({
    headerName: 'Verified',
    field: 'verifiedStatus',
    pinned: 'left',
    keyCreator: ({ value }) => {
      if (validVerifiedValues.has(value)) {
        return upperFirst(value);
      }
      return null;
    },
    valueFormatter: ({ value }) => {
      if (value) return upperFirst(value);
      return value;
    },
    filterParams: {
      // Required to allow filtering for blank values
      // See https://github.com/ag-grid/ag-grid/issues/4856
      caseSensitive: true,
    },
  });

  listingIdColumn = this.textColumn({
    headerName: 'Platform ID #',
    field: 'listingId',
    colId: LISTING_ID,
    cellClass: LISTING_ID,
  });

  listingUrlColumn = this.multiFilterColumn({
    headerName: 'Live',
    field: 'listingUrl',
    colId: 'listingUrl',
    cellClass: 'hyperlinks',
    cellRenderer: 'platformLinkCellRenderer',
    valueGetter: ({ data }) => ({
      live: data?.live ? 'Live' : 'Not-Live',
      listingUrl: data?.listingUrl,
      platform: data?.platform,
    }),
    comparator: (a, b) => {
      if (a?.platform === b?.platform) return 0;
      return a?.platform > b?.platform ? 1 : -1;
    },
    keyCreator: ({ value }) => [
      ListingColDefs.liveColumnPlatformKeyFactory(value?.platform),
      value?.live,
    ],
    filterParams: {
      filters: [
        {
          filter: 'agSetColumnFilter',
          filterParams: {
            suppressMiniFilter: true,
            // TODO: This should match the icons we have for each
            values: [
              ListingColDefs.platformKeys.airbnb,
              ListingColDefs.platformKeys.vrbo,
              ListingColDefs.platformKeys.flipkey,
              ListingColDefs.platformKeys.others,
              ListingColDefs.platformKeys.null,
            ],
            valueFormatter: ({ value }) => {
              if (value === 'true' || value === 'false') {
                return ListingColDefs.booleanLiveToStringGetter[value];
              }
              return null;
            },
            comparator: (a, b) => {
              if (a === b) return 0;
              const valA = ListingColDefs.liveFilterComparisonValue(a);
              const valB = ListingColDefs.liveFilterComparisonValue(b);
              return valA > valB ? 1 : -1;
            },
          },
        },
        {
          filter: 'agSetColumnFilter',
          filterParams: {
            suppressMiniFilter: true,
            values: ['Live', 'Not-Live'],
            valueFormatter: ({ value }) =>
              ListingColDefs.booleanLiveToStringGetter[value],
          },
        },
      ],
    },
  });

  pdfUrlColumn = this.noFiltersColumn({
    pinned: 'left',
    headerName: 'PDF',
    field: 'pdfUrl',
    colId: 'pdfUrl',
    cellClass: 'hyperlinks', // uses id to select styles in excelStyles (like css class)
    cellRenderer: 'pdfLinkCellRenderer',
    resizable: true,
    suppressMenu: true,
  });

  titleColumn = this.textColumn({
    headerName: 'Title',
    field: 'title',
    cellStyle: {
      cursor: 'pointer',
      textOverflow: 'ellipsis',
      display: 'block',
    },
    tooltipField: 'title',
    width: 250,
  });

  createdOnColumn = this.dateColumn({
    headerName: 'First Seen',
    field: 'createdOn',
    filter: 'agDateColumnFilter',
  });

  identifiedDateColumn = this.dateColumn({
    headerName: 'Identified Date',
    field: 'verifiedDate',
    filter: 'agDateColumnFilter',
    width: 140,
  });

  unitTypeColumn = this.textColumn({
    headerName: 'Unit Type',
    field: 'unitType',
    cellStyle: { cursor: 'pointer' },
    refData: subtypeChartLabels.unitType,
  });

  rentalStructureColumn = this.setColumn({
    headerName: 'Rental Structure',
    field: 'rentalStructure',
    cellStyle: { cursor: 'pointer' },
    refData: subtypeChartLabels.rentalStructure,
  });

  rentalTypeColumn = this.setColumn({
    headerName: 'Rental Type',
    field: 'propertyType',
    cellStyle: { cursor: 'pointer' },
    // using value getter so set filters display the transformed values
    valueGetter: ({ data }) =>
      this.keyMappings?.rentalType[data?.propertyType] || data?.propertyType,
  });

  minimumNightStayColumn = this.numberColumn({
    headerName: 'Minimum Night Stay',
    field: 'minimumStayAdvertised',
    cellStyle: { cursor: 'pointer' },
  });

  violationsColumn = this.setColumn({
    headerName: 'Possible Violations',
    field: 'violations',
    cellStyle: {
      cursor: 'pointer',
      textOverflow: 'ellipsis',
      display: 'block',
    },
    tooltipField: 'violations',
    width: 250,
  });

  // ----- longTermStayColGroup -----

  minimumStayAdvertisedColumn = this.numberColumn({
    headerName: 'Advertised',
    field: 'minimumStayAdvertised',
  });

  // ----- licenseComplianceColGroup -----
  licenseDisplayedColumn = this.textColumn({
    headerName: 'Displayed',
    // FIXME: Refactor. Inheritance will not work outside a function. May confuse someone
    valueGetter: ({ data }) => data?.[this.licenseDisplayedColumnFieldName],
  });

  licenseColumn = this.textColumn({
    headerName: 'License #',
    pinned: 'left',
    // FIXME: Turn columns into getters and colGroups into functions to solve inheritance issues
    valueGetter: ({ data }) => data?.[this.licenseColumnFieldName],
    width: 140,
  });

  licenseIssueDateColumn = this.dateColumn({
    headerName: 'Issue Date',
    field: 'issuedDate',
    width: 140,
  });

  licenseRegistrantEmailColumn = this.textColumn({
    headerName: 'Registrant Email Address',
    field: 'registrantEmail',
  });

  lastInspectionDateColumn = this.textColumn({
    headerName: 'Last Inspection Date',
    field: 'lastInspectionDate',
  });

  lastInspectionResultColumn = this.textColumn({
    headerName: 'Inspection Result',
    field: 'lastInspectionResult',
    valueGetter: ({ data }) => InspectionText[data?.lastInspectionResult],
  });

  // ----- occupancyComplianceColGroup -----
  occupancyAdvertisedColumn = this.numberColumn({
    headerName: 'Advertised',
    field: 'occupancyAdvertised',
  });

  occupancyAllowedColumn = this.numberColumn({
    headerName: 'Licensed',
    field: 'occupancyAllowed',
  });

  // ----- bedroomInfoColGroup -----
  bedroomsAdvertisedColumn = this.numberColumn({
    headerName: 'Advertised',
    field: 'bedroomsAdvertised',
  });

  bedroomsColumn = this.numberColumn({
    headerName: 'Licensed',
    field: 'bedroomsLicensed',
  });

  bedroomsLicensedColumn = this.numberColumn({
    headerName: 'Public Record',
    field: 'bedrooms',
  });

  // ----- otherInfoColGroup -----

  bathroomsColumn = this.numberColumn({
    headerName: 'Bathrooms',
    field: 'bathrooms',
  });

  // ----- bookingInfoColGroup -----

  futureActivityColumn = this.textColumn({
    headerName: 'Future Activity',
    field: 'activity',
  });

  nightsBookedToDateColumn = this.numberColumn({
    headerName: 'Nights Booked To Date (Year-to-date)',
    maxWidth: 200,
    field: 'nightsBookedYtd',
  });

  bookingFrequencyColumn = this.numberColumn({
    headerName: 'Booking Frequency (AVG last 12 months)',
    maxWidth: 160,
    field: 'bookingFreqLast12Months',
  });

  estimatedTOTColumn = this.numberColumn({
    headerValueGetter: () => `Estimated ${this.totAlias} (Year-to-date)`,
    maxWidth: 150,
    valueGetter: ({ data }) =>
      data?.estimatedTotYtd ? `$${data.estimatedTotYtd}` : null,
  });

  averagePriceDayColumn = this.numberColumn({
    headerName: 'Average Price/Day (Year-to-date)',
    maxWidth: 150,
    valueGetter: ({ data }) =>
      data?.avgPriceYtd ? `$${data.avgPriceYtd}` : null,
  });

  /*
   * -------------------------
   * ----- column groups -----
   * -------------------------
   */

  propertyDetailsColGroup = {
    headerName: 'Property details',
    children: [
      this.groupByAddressColumn,
      // this.diffStatusColumn, /* Hiding these two columns as requested by Eileen */
      // this.lastUpdatedColumn,
      this.unitNumberColumn,
      this.streetAddressColumn,
      this.cityColumn,
      this.stateColumn,
      this.zipColumn,

      this.apnColumn,
      this.deckardIDColumn,
      this.zoneColumn,
      this.ownerColumn,
      this.ownerOccupiedColumn,
      this.residentialTypeColumn,
      this.ownerMailingAddressColumn,
      this.ownerUnitNumberColumn,
      this.ownerStreetAddressColumn,
      this.ownerCityColumn,
      this.ownerStateColumn,
      this.ownerZipColumn,

      this.identifiedDateColumn,
    ],
  };

  listingsInfoColGroup = {
    headerName: 'Listing information',
    children: [
      this.hostColumn,
      this.listingUrlColumn,
      this.pdfUrlColumn,
      this.verifiedColumn,
      this.titleColumn,
      this.createdOnColumn,
      this.unitTypeColumn,
      this.rentalStructureColumn,
      this.rentalTypeColumn,
      this.minimumNightStayColumn,
      this.violationsColumn,
    ],
  };

  longTermStayComplianceColGroup = {
    headerName: 'Long Term Stay Compliance',
    children: [this.minimumStayAdvertisedColumn],
  };

  licenseComplianceColGroup = {
    headerName: 'License Compliance',
    children: [
      this.licenseDisplayedColumn,
      this.licenseColumn,
      this.licenseIssueDateColumn,
      this.licenseRegistrantEmailColumn,
      this.lastInspectionDateColumn,
      this.lastInspectionResultColumn,
    ],
  };

  occupancyComplianceColGroup = {
    headerName: 'Occupancy Compliance',
    children: [this.occupancyAdvertisedColumn, this.occupancyAllowedColumn],
  };

  bedroomInfoColGroup = {
    headerName: 'Bedroom Information',
    children: [
      this.bedroomsAdvertisedColumn,
      this.bedroomsLicensedColumn,
      this.bedroomsColumn,
    ],
  };

  otherInfoColGroup = {
    headerName: 'Other Information',
    children: [this.bathroomsColumn],
  };

  bookingInfoColGroup = {
    headerName: 'Booking Information',
    children: [
      this.futureActivityColumn,
      this.nightsBookedToDateColumn,
      this.bookingFrequencyColumn,
      this.estimatedTOTColumn,
      this.averagePriceDayColumn,
    ],
  };

  /**
   * This method returns the Ag-Grid column definitions for Listing grids
   *
   * @param {{ ganttMonthDetails }} params contains data for building gant chart
   */
  generateAgGridColDefs() {
    return {
      autoGroupColumnDef: this.autoGroupColDef,
      columnGroups: this.columnGroups(),
    };
  }

  appendColumnToSet = (set: TColumn[], column: TColumn) => {
    set.push(column);
  };

  insertColumnToSet = (
    set: TColumn[],
    column: TColumn,
    insertionIndex: number // Note that this should be 0-indexed
  ) => {
    if (insertionIndex === -1) {
      this.appendColumnToSet(set, column);
    } else {
      // Fallback to appending when no matches
      set.splice(insertionIndex + 1, 0, column); // "+1" as we're inserting "behind"
    }
  };

  // This only inserts text rendering columns (refer to "this.textColumn") into the list.
  addColumnsToList = (columns?: TCustomColumnDefinitions): void => {
    if (!Array.isArray(columns) && (isNil(columns) || isEmpty(columns))) return;

    columns.forEach((columnDefinition: TCustomColumnDefinition) => {
      const {
        name: field,
        displayName: headerName,
        colGroup,
        pinned,
      } = columnDefinition;

      const colGroupField =
        BaseColDefs.configurableColGroupIdToColDefFactoryField[colGroup] ||
        'otherInfoColGroup';

      const copiedColumnSet: TColumn[] = [...this[colGroupField].children];
      const toBeAddedColumn: TColumn = this.textColumn({
        headerName,
        pinned,
        field,
        width: columnDefinition?.width,
      });

      if (columnDefinition.behindColumnByField) {
        const matchingColumnIndex = copiedColumnSet.findIndex(
          (col) => col.field === columnDefinition.behindColumnByField
        );
        this.insertColumnToSet(
          copiedColumnSet,
          toBeAddedColumn,
          matchingColumnIndex
        );
      } else {
        // By default if no extra instructions are provided, then just append the column to the group
        this.appendColumnToSet(copiedColumnSet, toBeAddedColumn);
      }

      this[colGroupField] = {
        ...this[colGroupField],
        children: copiedColumnSet,
      };
    });
  };
}
